/**
 * InlineLauncher — host-DOM launcher button that mounts beside a page element
 * (e.g. header search). Follows the same config-driven pattern as
 * `createFloatingLauncher`: the chat widget runtime calls
 * `createInlineLauncher()` when `config.inlineLauncher` is set.
 *
 * In the injector, pass selectors and styling through chat config:
 *
 *   chat: {
 *     inlineLauncher: {
 *       hostSelector: '#searchBoxComponentID',
 *       siblingSelector: '.mini-search',
 *       siblingPlacement: 'before',
 *       title: 'Ask our expert',
 *       iconUrl: 'https://…/avatar.png',
 *       primaryColor: '#ec6e00',
 *     },
 *   }
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface InlineLauncherMobileHostOptions {
  /** Host element for the inline row on compact viewports. */
  hostSelector: string;
  /** Sibling co-located in the row (e.g. mobile search trigger). */
  siblingSelector?: string;
  siblingPlacement?: 'before' | 'after';
  siblingRestoreParentSelector?: string;
  rowPlacement?: 'start' | 'end';
}

export interface InlineLauncherSizeOptions {
  /** Gap between sibling search and launcher button in the row. */
  rowGap?: string;
  /** Button min-height. */
  buttonHeight?: string;
  /** Button padding. */
  buttonPadding?: string;
  /** Gap between icon, title and spark inside the button. */
  buttonGap?: string;
  /** Title font size. */
  fontSize?: string;
  /** Avatar wrapper size. */
  iconSize?: string;
  /** Avatar image size. */
  iconImageSize?: string;
  /** Spark wrapper size. */
  sparkSize?: string;
  /** Spark SVG size. */
  sparkIconSize?: string;
}

export interface InlineLauncherOptions {
  /** Host element that receives the inline launcher row. */
  hostSelector: string;

  /** CSS class for the flex row wrapper. Default: 'gengage-inline-launcher-row' */
  rowClassName?: string;

  /** Optional sibling element co-located in the row (e.g. site search input). */
  siblingSelector?: string;

  /**
   * Sibling position relative to the launcher button.
   * `'before'` → sibling first, button after (search + launcher).
   * Default: `'before'`.
   */
  siblingPlacement?: 'before' | 'after';

  /**
   * Where the flex row is inserted inside the host when first created.
   * `'start'` keeps dropdown/sibling overlays below the search row in compact header layouts.
   * Default: `'end'`.
   */
  rowPlacement?: 'start' | 'end';

  /**
   * Parent element that receives the sibling when the inline row is torn down.
   * Defaults to `hostSelector`.
   */
  siblingRestoreParentSelector?: string;

  /** Visible label text. */
  title?: string;

  /** Avatar / icon image URL. */
  iconUrl?: string;

  /** Brand accent color. Default: '#6366f1' */
  primaryColor?: string;

  /** Body text color. Default: '#222222' */
  textColor?: string;

  /** CSS font-family string. Default: 'inherit' */
  fontFamily?: string;

  /** Button corner radius. Default: '3px' */
  borderRadius?: string;

  /** Accessible name for the button. Falls back to `title`. */
  ariaLabel?: string;

  /** Show the decorative spark icon. Default: true */
  showSparkIcon?: boolean;

  /**
   * Minimum viewport width (inclusive) required to mount inline on desktop.
   * Below this width the row is removed and the floating launcher is shown,
   * unless `enableOnMobile` is true.
   * Default: 0 (always eligible when page type allows).
   */
  desktopMinWidth?: number;

  /**
   * When true, inline launcher also mounts below `desktopMinWidth` (mobile/tablet).
   * Default: false.
   */
  enableOnMobile?: boolean;

  /**
   * Page types where inline launcher must not mount (floating launcher shown instead).
   * Default: `['pdp']`.
   */
  disabledPageTypes?: string[];

  /**
   * Hide the shadow-DOM floating launcher while inline is mounted.
   * The chat runtime also keeps the floating launcher hidden during the first
   * inline mount attempt so delayed host discovery does not flash the floating
   * launcher before the inline row settles.
   * Default: true.
   */
  hideFloatingLauncher?: boolean;

  /** Max animation frames to wait for host/sibling DOM. Default: 90. */
  mountPollFrames?: number;

  /** Debounce for resize/orientation sync in ms. Default: 90. */
  debounceMs?: number;

  /** Stable id for the launcher button. Default: 'gengage-inline-launcher'. */
  buttonId?: string;

  /**
   * Optional DOM targets for compact viewports when `enableOnMobile` is true
   * and width is below `desktopMinWidth`. Falls back to desktop selectors when omitted.
   */
  mobileHost?: InlineLauncherMobileHostOptions;

  /**
   * Optional size overrides. Desktop values apply by default; `size.mobile`
   * applies below the compact breakpoint (`desktopMinWidth` when `enableOnMobile`, else 576px).
   */
  size?: InlineLauncherSizeOptions & {
    mobile?: InlineLauncherSizeOptions & {
      hideTitle?: boolean;
      hideSpark?: boolean;
      hideLogo?: boolean;
    };
  };
}

export interface InlineLauncherKit {
  /** Whether the inline row is currently mounted in the host DOM. */
  readonly isMounted: boolean;

  /** Attempt mount/unmount based on viewport + page type. Returns true when mounted. */
  sync(pageType?: string | undefined): Promise<boolean>;

  /** Toggle active styling while the chat drawer is open. */
  setActive(active: boolean): void;

  /** Release listeners, restore sibling placement, remove injected DOM. */
  destroy(): void;
}

interface InlineLauncherDeps {
  shadowRoot: ShadowRoot | null;
  getPageType: () => string | undefined;
}

interface InlineLauncherCallbacks {
  onClick: () => void;
}

interface ResolvedMountConfig {
  hostSelector: string;
  siblingSelector?: string | undefined;
  siblingRestoreParentSelector: string;
  rowPlacement: 'start' | 'end';
  siblingPlacement: 'before' | 'after';
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const STYLE_ID = 'gengage-inline-launcher-style';
const HIDE_FLOATING_STYLE_ID = 'gengage-inline-launcher-hide-floating';
const BUTTON_CLASS = 'gengage-inline-launcher';
const DEFAULT_BUTTON_ID = 'gengage-inline-launcher';
const SIBLING_DATA_ATTR = 'data-gengage-inline-sibling';
const SIBLING_RESTORE_INDEX_ATTR = 'data-gengage-inline-sibling-restore-index';

const DEFAULT_SPARK_SVG =
  '<svg viewBox="0 0 20 20" fill="none" aria-hidden="true">' +
  '<path d="M8.85 2.4c.23 0 .43.16.48.39l.9 3.77a3.8 3.8 0 0 0 2.8 2.8l3.77.9a.5.5 0 0 1 0 .98l-3.77.9a3.8 3.8 0 0 0-2.8 2.8l-.9 3.77a.5.5 0 0 1-.98 0l-.9-3.77a3.8 3.8 0 0 0-2.8-2.8l-3.77-.9a.5.5 0 0 1 0-.98l3.77-.9a3.8 3.8 0 0 0 2.8-2.8l.9-3.77c.05-.23.25-.39.48-.39Z" fill="currentColor"/>' +
  '<path d="M15.95 1.1c.12 0 .23.08.26.2l.32 1.34c.16.68.7 1.21 1.37 1.37l1.34.32a.27.27 0 0 1 0 .53l-1.34.32a1.87 1.87 0 0 0-1.37 1.37l-.32 1.34a.27.27 0 0 1-.53 0l-.32-1.34A1.87 1.87 0 0 0 14 5.18l-1.34-.32a.27.27 0 0 1 0-.53L14 4a1.87 1.87 0 0 0 1.37-1.37l.32-1.34c.03-.12.14-.2.26-.2Z" fill="currentColor" opacity=".72"/>' +
  '</svg>';

// ---------------------------------------------------------------------------
// CSS
// ---------------------------------------------------------------------------

interface ResolvedInlineLauncherSizes {
  rowGap: string;
  buttonHeight: string;
  buttonPadding: string;
  buttonGap: string;
  fontSize: string;
  iconSize: string;
  iconImageSize: string;
  sparkSize: string;
  sparkIconSize: string;
  mobile: {
    rowGap: string;
    buttonPadding: string;
    buttonGap: string;
    fontSize: string;
    iconSize: string;
    iconImageSize: string;
    sparkSize: string;
    sparkIconSize: string;
    hideTitle: boolean;
    hideSpark: boolean;
    hideLogo: boolean;
  };
}

const DEFAULT_INLINE_LAUNCHER_SIZES: ResolvedInlineLauncherSizes = {
  rowGap: '14px',
  buttonHeight: '46px',
  buttonPadding: '0 14px 0 10px',
  buttonGap: '10px',
  fontSize: '15px',
  iconSize: '30px',
  iconImageSize: '22px',
  sparkSize: '20px',
  sparkIconSize: '18px',
  mobile: {
    rowGap: '6px',
    buttonPadding: '0 8px 0 6px',
    buttonGap: '6px',
    fontSize: '12px',
    iconSize: '30px',
    iconImageSize: '22px',
    sparkSize: '14px',
    sparkIconSize: '12px',
    hideTitle: false,
    hideSpark: true,
    hideLogo: false,
  },
};

function resolveInlineLauncherSizes(size?: InlineLauncherOptions['size']): ResolvedInlineLauncherSizes {
  const mobile = size?.mobile;
  return {
    rowGap: size?.rowGap ?? DEFAULT_INLINE_LAUNCHER_SIZES.rowGap,
    buttonHeight: size?.buttonHeight ?? DEFAULT_INLINE_LAUNCHER_SIZES.buttonHeight,
    buttonPadding: size?.buttonPadding ?? DEFAULT_INLINE_LAUNCHER_SIZES.buttonPadding,
    buttonGap: size?.buttonGap ?? DEFAULT_INLINE_LAUNCHER_SIZES.buttonGap,
    fontSize: size?.fontSize ?? DEFAULT_INLINE_LAUNCHER_SIZES.fontSize,
    iconSize: size?.iconSize ?? DEFAULT_INLINE_LAUNCHER_SIZES.iconSize,
    iconImageSize: size?.iconImageSize ?? DEFAULT_INLINE_LAUNCHER_SIZES.iconImageSize,
    sparkSize: size?.sparkSize ?? DEFAULT_INLINE_LAUNCHER_SIZES.sparkSize,
    sparkIconSize: size?.sparkIconSize ?? DEFAULT_INLINE_LAUNCHER_SIZES.sparkIconSize,
    mobile: {
      rowGap: mobile?.rowGap ?? DEFAULT_INLINE_LAUNCHER_SIZES.mobile.rowGap,
      buttonPadding: mobile?.buttonPadding ?? DEFAULT_INLINE_LAUNCHER_SIZES.mobile.buttonPadding,
      buttonGap: mobile?.buttonGap ?? DEFAULT_INLINE_LAUNCHER_SIZES.mobile.buttonGap,
      fontSize: mobile?.fontSize ?? DEFAULT_INLINE_LAUNCHER_SIZES.mobile.fontSize,
      iconSize: mobile?.iconSize ?? DEFAULT_INLINE_LAUNCHER_SIZES.mobile.iconSize,
      iconImageSize: mobile?.iconImageSize ?? DEFAULT_INLINE_LAUNCHER_SIZES.mobile.iconImageSize,
      sparkSize: mobile?.sparkSize ?? DEFAULT_INLINE_LAUNCHER_SIZES.mobile.sparkSize,
      sparkIconSize: mobile?.sparkIconSize ?? DEFAULT_INLINE_LAUNCHER_SIZES.mobile.sparkIconSize,
      hideTitle: mobile?.hideTitle ?? DEFAULT_INLINE_LAUNCHER_SIZES.mobile.hideTitle,
      hideSpark: mobile?.hideSpark ?? DEFAULT_INLINE_LAUNCHER_SIZES.mobile.hideSpark,
      hideLogo: mobile?.hideLogo ?? DEFAULT_INLINE_LAUNCHER_SIZES.mobile.hideLogo,
    },
  };
}

function buildCSS(opts: {
  primaryColor: string;
  textColor: string;
  fontFamily: string;
  borderRadius: string;
  rowClassName: string;
  compactBelowWidth: number;
  buttonId: string;
  sizes: ResolvedInlineLauncherSizes;
}): string {
  const row = `.${opts.rowClassName}`;
  const btn = `#${CSS.escape(opts.buttonId)}`;
  const icon = `.${BUTTON_CLASS}-icon`;
  const copy = `.${BUTTON_CLASS}-copy`;
  const title = `.${BUTTON_CLASS}-title`;
  const spark = `.${BUTTON_CLASS}-spark`;
  const compactMq = `@media (max-width: ${opts.compactBelowWidth - 0.02}px)`;
  const { sizes } = opts;
  const mobileCopyRule = sizes.mobile.hideTitle ? 'display:none;' : 'display:flex;flex:0 1 auto;min-width:0;';
  const mobileSparkRule = sizes.mobile.hideSpark ? 'display:none;' : 'display:inline-flex;';
  const mobileIconRule = sizes.mobile.hideLogo
    ? 'display:none !important;width:0 !important;height:0 !important;flex:0 0 0 !important;overflow:hidden !important;'
    : 'display:inline-flex;';
  return `
${row}{
  display:flex;
  align-items:center;
  gap:${sizes.rowGap};
  min-width:0;
}
${row} > [${SIBLING_DATA_ATTR}]{
  flex:1 1 auto;
  min-width:0;
}
${btn}{
  flex:0 0 auto;
  display:inline-flex;
  align-items:center;
  gap:${sizes.buttonGap};
  min-height:${sizes.buttonHeight};
  padding:${sizes.buttonPadding};
  border:1px solid color-mix(in srgb, ${opts.primaryColor} 52%, white);
  border-radius:${opts.borderRadius};
  background:linear-gradient(180deg,#fffdfa 0%,#fff2e6 100%);
  color:${opts.textColor};
  box-shadow:0 10px 22px color-mix(in srgb, ${opts.primaryColor} 14%, transparent);
  font:600 ${sizes.fontSize}/1.05 ${opts.fontFamily};
  letter-spacing:-0.01em;
  cursor:pointer;
  transition:transform .18s ease, box-shadow .18s ease, border-color .18s ease, background .18s ease;
  white-space:nowrap;
  position:relative;
}
${btn}::before{
  content:"";
  position:absolute;
  inset:1px;
  border-radius:inherit;
  background:linear-gradient(180deg,rgba(255,255,255,.92),rgba(255,255,255,.72));
  z-index:0;
}
${btn} > *{
  position:relative;
  z-index:1;
}
${btn}:hover{
  transform:translateY(-1px);
  border-color:${opts.primaryColor};
  box-shadow:0 14px 26px color-mix(in srgb, ${opts.primaryColor} 20%, transparent);
}
${btn}:focus-visible{
  outline:2px solid color-mix(in srgb, ${opts.primaryColor} 34%, white);
  outline-offset:2px;
}
${btn}.${BUTTON_CLASS}--active{
  border-color:${opts.primaryColor};
  background:linear-gradient(180deg,#fff7ef 0%,#ffe7d5 100%);
  box-shadow:0 16px 28px color-mix(in srgb, ${opts.primaryColor} 24%, transparent);
}
${btn} ${icon}{
  display:inline-flex;
  align-items:center;
  justify-content:center;
  width:${sizes.iconSize};
  height:${sizes.iconSize};
  flex:0 0 ${sizes.iconSize};
  border-radius:10px;
  background:linear-gradient(180deg,#fff7f0 0%,#ffe8d6 100%);
  box-shadow:inset 0 0 0 1px color-mix(in srgb, ${opts.primaryColor} 12%, transparent);
}
${btn} ${icon} img{
  width:${sizes.iconImageSize};
  height:${sizes.iconImageSize};
  object-fit:contain;
}
${btn} ${copy}{
  display:flex;
  align-items:center;
  min-width:0;
  flex:1 1 auto;
}
${btn} ${title}{
  display:inline-flex;
  align-items:center;
  font-weight:700;
  color:#2f2f2f;
}
${btn} ${spark}{
  display:inline-flex;
  align-items:center;
  justify-content:center;
  width:${sizes.sparkSize};
  height:${sizes.sparkSize};
  flex:0 0 ${sizes.sparkSize};
  color:${opts.primaryColor};
}
${btn} ${spark} svg{
  width:${sizes.sparkIconSize};
  height:${sizes.sparkIconSize};
  display:block;
}
${compactMq}{
  ${row}{ gap:${sizes.mobile.rowGap}; }
  ${btn}{
    gap:${sizes.mobile.buttonGap};
    padding:${sizes.mobile.buttonPadding};
  }
  ${btn} ${copy}{
    ${mobileCopyRule}
  }
  ${btn} ${title}{
    font-size:${sizes.mobile.fontSize};
    line-height:1.05;
  }
  ${btn} ${icon}{
    ${mobileIconRule}
  }
  ${btn} ${spark}{
    ${mobileSparkRule}
    width:${sizes.mobile.sparkSize};
    height:${sizes.mobile.sparkSize};
    flex:0 0 ${sizes.mobile.sparkSize};
  }
  ${btn} ${spark} svg{
    width:${sizes.mobile.sparkIconSize};
    height:${sizes.mobile.sparkIconSize};
  }
}
`.trim();
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

// ---------------------------------------------------------------------------
// Factory
// ---------------------------------------------------------------------------

export function createInlineLauncher(
  options: InlineLauncherOptions,
  callbacks: InlineLauncherCallbacks,
  deps: InlineLauncherDeps,
): InlineLauncherKit {
  const {
    hostSelector,
    rowClassName = 'gengage-inline-launcher-row',
    siblingSelector,
    siblingPlacement = 'before',
    rowPlacement = 'end',
    title = 'Ask',
    iconUrl,
    primaryColor = '#6366f1',
    textColor = '#222222',
    fontFamily = 'inherit',
    borderRadius = '3px',
    showSparkIcon = true,
    desktopMinWidth = 0,
    enableOnMobile = false,
    disabledPageTypes = ['pdp'],
    hideFloatingLauncher = true,
    mountPollFrames = 90,
    debounceMs = 90,
    buttonId = DEFAULT_BUTTON_ID,
  } = options;

  const ariaLabel = options.ariaLabel ?? title;
  const defaultSiblingRestoreParentSelector = options.siblingRestoreParentSelector ?? hostSelector;
  const compactBelowWidth = enableOnMobile && desktopMinWidth > 0 ? desktopMinWidth : 576;
  const resolvedSizes = resolveInlineLauncherSizes(options.size);
  const styleId = `${STYLE_ID}-${buttonId}`;
  let mounted = false;
  let active = false;
  let syncTimer: number | null = null;
  let buttonEl: HTMLButtonElement | null = null;
  let rowEl: HTMLElement | null = null;
  let buttonClickHandler: (() => void) | null = null;
  let lastMount: ResolvedMountConfig | null = null;
  let siblingEl: HTMLElement | null = null;
  let syncSeq = 0;

  const resolveMountConfig = (): ResolvedMountConfig => {
    const useMobileHost =
      enableOnMobile && desktopMinWidth > 0 && window.innerWidth < desktopMinWidth && options.mobileHost;
    if (useMobileHost && options.mobileHost) {
      const mobile = options.mobileHost;
      return {
        hostSelector: mobile.hostSelector,
        siblingSelector: mobile.siblingSelector ?? siblingSelector,
        siblingRestoreParentSelector: mobile.siblingRestoreParentSelector ?? mobile.hostSelector,
        rowPlacement: mobile.rowPlacement ?? rowPlacement,
        siblingPlacement: mobile.siblingPlacement ?? siblingPlacement,
      };
    }
    return {
      hostSelector,
      siblingSelector,
      siblingRestoreParentSelector: defaultSiblingRestoreParentSelector,
      rowPlacement,
      siblingPlacement,
    };
  };

  const mountConfigKey = (ctx: ResolvedMountConfig): string =>
    [ctx.hostSelector, ctx.siblingSelector ?? '', ctx.siblingRestoreParentSelector].join('|');

  const injectStyles = (): void => {
    if (document.getElementById(styleId)) return;
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = buildCSS({
      primaryColor,
      textColor,
      fontFamily,
      borderRadius,
      rowClassName,
      compactBelowWidth,
      buttonId,
      sizes: resolvedSizes,
    });
    document.head.appendChild(style);
  };

  const setFloatingHidden = (hidden: boolean): void => {
    if (!hideFloatingLauncher || !deps.shadowRoot) return;
    const existing = deps.shadowRoot.getElementById(HIDE_FLOATING_STYLE_ID);
    if (!hidden) {
      existing?.remove();
      return;
    }
    if (existing) return;
    const el = document.createElement('style');
    el.id = HIDE_FLOATING_STYLE_ID;
    el.textContent = '.gengage-chat-launcher-container,.gengage-fl{display:none!important;}';
    deps.shadowRoot.appendChild(el);
  };

  const findHost = (selector: string): HTMLElement | null => {
    const host = document.querySelector(selector);
    return host instanceof HTMLElement ? host : null;
  };

  const findSibling = (selector?: string): HTMLElement | null => {
    if (!selector) return null;
    const sibling = document.querySelector(selector);
    return sibling instanceof HTMLElement ? sibling : null;
  };

  const resolveSibling = (ctx: ResolvedMountConfig): HTMLElement | null => {
    if (siblingEl?.isConnected) return siblingEl;
    const sibling = findSibling(ctx.siblingSelector);
    if (sibling) siblingEl = sibling;
    return sibling;
  };

  const findSiblingRestoreParent = (selector: string): HTMLElement | null => {
    const parent = document.querySelector(selector);
    return parent instanceof HTMLElement ? parent : null;
  };

  const rememberSiblingRestoreIndex = (sibling: HTMLElement): void => {
    if (sibling.hasAttribute(SIBLING_RESTORE_INDEX_ATTR)) return;
    const parent = sibling.parentElement;
    if (!parent) return;
    const index = Array.from(parent.children).indexOf(sibling);
    if (index >= 0) {
      sibling.setAttribute(SIBLING_RESTORE_INDEX_ATTR, String(index));
    }
  };

  const restoreSibling = (ctx: ResolvedMountConfig): void => {
    const sibling = resolveSibling(ctx);
    const restoreParent = findSiblingRestoreParent(ctx.siblingRestoreParentSelector);
    if (!sibling || !restoreParent) return;

    rememberSiblingRestoreIndex(sibling);

    if (sibling.parentElement === restoreParent) {
      sibling.removeAttribute(SIBLING_DATA_ATTR);
      return;
    }

    const rawIndex = sibling.getAttribute(SIBLING_RESTORE_INDEX_ATTR);
    const index = rawIndex ? Number.parseInt(rawIndex, 10) : 0;
    const children = Array.from(restoreParent.children).filter(
      (child) => !(child instanceof HTMLElement && child.classList.contains(rowClassName)),
    );
    const anchor = Number.isFinite(index) && index >= 0 ? (children[index] ?? null) : null;
    if (anchor) {
      restoreParent.insertBefore(sibling, anchor);
      sibling.removeAttribute(SIBLING_DATA_ATTR);
      return;
    }
    restoreParent.appendChild(sibling);
    sibling.removeAttribute(SIBLING_DATA_ATTR);
  };

  const removeRow = (host: HTMLElement): void => {
    const row = host.querySelector(`.${rowClassName}`);
    row?.remove();
  };

  const createIconElement = (): HTMLSpanElement => {
    const iconWrap = document.createElement('span');
    iconWrap.className = `${BUTTON_CLASS}-icon`;
    const icon = document.createElement('img');
    icon.src = iconUrl!;
    icon.alt = '';
    icon.draggable = false;
    iconWrap.appendChild(icon);
    return iconWrap;
  };

  const isCompactViewport = (): boolean => window.innerWidth < compactBelowWidth;

  const syncButtonLogo = (button: HTMLButtonElement): void => {
    if (!iconUrl) return;
    const iconWrap = button.querySelector(`.${BUTTON_CLASS}-icon`);
    const hide = isCompactViewport() && resolvedSizes.mobile.hideLogo;
    if (hide) {
      iconWrap?.remove();
      return;
    }
    if (!iconWrap) {
      button.insertBefore(createIconElement(), button.firstChild);
    }
  };

  const createButton = (): HTMLButtonElement => {
    const button = document.createElement('button');
    button.type = 'button';
    button.id = buttonId;
    button.className = BUTTON_CLASS;
    button.setAttribute('aria-label', ariaLabel);

    if (iconUrl) {
      button.appendChild(createIconElement());
    }

    const copy = document.createElement('span');
    copy.className = `${BUTTON_CLASS}-copy`;
    const titleEl = document.createElement('span');
    titleEl.className = `${BUTTON_CLASS}-title`;
    titleEl.textContent = title;
    copy.appendChild(titleEl);
    button.appendChild(copy);

    if (showSparkIcon) {
      const spark = document.createElement('span');
      spark.className = `${BUTTON_CLASS}-spark`;
      spark.innerHTML = DEFAULT_SPARK_SVG;
      button.appendChild(spark);
    }

    buttonClickHandler = () => callbacks.onClick();
    button.addEventListener('click', buttonClickHandler);
    return button;
  };

  const placeSibling = (
    row: HTMLElement,
    sibling: HTMLElement,
    button: HTMLButtonElement,
    ctx: ResolvedMountConfig,
  ): void => {
    rememberSiblingRestoreIndex(sibling);
    sibling.setAttribute(SIBLING_DATA_ATTR, 'true');
    if (ctx.siblingPlacement === 'before') {
      if (sibling.parentElement !== row) {
        row.insertBefore(sibling, button);
      } else if (sibling.nextElementSibling !== button) {
        row.insertBefore(sibling, button);
      }
      return;
    }
    if (sibling.parentElement !== row) {
      row.appendChild(sibling);
    } else if (button.nextElementSibling !== sibling) {
      row.appendChild(sibling);
    }
  };

  const teardown = (): void => {
    if (lastMount) {
      restoreSibling(lastMount);
      // Remove the row by reference first — when the SPA has replaced the host,
      // re-querying via the host selector no longer finds the detached row.
      rowEl?.remove();
      const host = findHost(lastMount.hostSelector);
      if (host) {
        removeRow(host);
      }
      lastMount = null;
    }
    if (buttonEl && buttonClickHandler) {
      buttonEl.removeEventListener('click', buttonClickHandler);
    }
    buttonClickHandler = null;
    rowEl = null;
    siblingEl = null;
    setFloatingHidden(false);
    mounted = false;
  };

  const shouldEnable = (pageType: string | undefined): boolean => {
    if (pageType && disabledPageTypes.includes(pageType)) return false;
    if (desktopMinWidth > 0 && window.innerWidth < desktopMinWidth && !enableOnMobile) return false;
    return true;
  };

  const syncNow = async (pageType?: string | undefined): Promise<boolean> => {
    // Reentrancy guard: overlapping callers (e.g. the staggered retries in
    // runtime + resize/orientation debounce) interleave at every `await sleep`.
    // A later call bumps syncSeq; earlier callers bail after their next yield so
    // only the newest mount loop mutates the DOM.
    const runId = ++syncSeq;
    const resolvedPageType = pageType ?? deps.getPageType();
    if (!shouldEnable(resolvedPageType)) {
      teardown();
      return false;
    }

    injectStyles();
    const ctx = resolveMountConfig();
    if (lastMount && mountConfigKey(lastMount) !== mountConfigKey(ctx)) {
      teardown();
    }

    for (let frame = 0; frame < mountPollFrames; frame += 1) {
      if (!shouldEnable(resolvedPageType)) {
        teardown();
        return false;
      }

      const activeCtx = resolveMountConfig();
      const host = findHost(activeCtx.hostSelector);
      const sibling = resolveSibling(activeCtx);
      if (host && (!activeCtx.siblingSelector || sibling)) {
        if (lastMount && mountConfigKey(lastMount) !== mountConfigKey(activeCtx)) {
          teardown();
        }

        if (sibling) {
          rememberSiblingRestoreIndex(sibling);
        }

        let row = host.querySelector(`.${rowClassName}`);
        if (!(row instanceof HTMLElement)) {
          row = document.createElement('div');
          row.className = rowClassName;
          if (activeCtx.rowPlacement === 'start') {
            host.insertBefore(row, host.firstChild);
          } else {
            host.appendChild(row);
          }
        }
        rowEl = row as HTMLElement;

        let button = row.querySelector<HTMLButtonElement>(`#${CSS.escape(buttonId)}`);
        if (!(button instanceof HTMLButtonElement)) {
          button = createButton();
          buttonEl = button;
          row.appendChild(button);
        } else {
          buttonEl = button;
        }

        if (sibling) {
          placeSibling(row as HTMLElement, sibling, button, activeCtx);
        }

        syncButtonLogo(button);

        if (active) {
          button.classList.add(`${BUTTON_CLASS}--active`);
        } else {
          button.classList.remove(`${BUTTON_CLASS}--active`);
        }

        setFloatingHidden(true);
        mounted = true;
        lastMount = activeCtx;
        return true;
      }

      await sleep(16);
      if (runId !== syncSeq) return false;
    }

    teardown();
    return false;
  };

  const scheduleSync = (): void => {
    if (syncTimer !== null) window.clearTimeout(syncTimer);
    syncTimer = window.setTimeout(() => {
      syncTimer = null;
      void syncNow();
    }, debounceMs);
  };

  const onResize = (): void => scheduleSync();
  window.addEventListener('resize', onResize, { passive: true });
  window.addEventListener('orientationchange', onResize);

  return {
    get isMounted() {
      return mounted;
    },

    sync(pageType?: string | undefined) {
      return syncNow(pageType);
    },

    setActive(isActive: boolean) {
      active = isActive;
      buttonEl?.classList.toggle(`${BUTTON_CLASS}--active`, isActive);
    },

    destroy() {
      if (syncTimer !== null) {
        window.clearTimeout(syncTimer);
        syncTimer = null;
      }
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
      teardown();
      document.getElementById(styleId)?.remove();
      buttonEl = null;
    },
  };
}
